/**
 * Centralized error message extraction for API calls.
 *
 * Two helpers cover the two situations:
 *   1. The fetch resolved but with !res.ok  -> use `extractApiErrorMessage(res, fallback)` (async)
 *   2. The fetch threw (network/CORS/abort) -> use `networkErrorMessage(error)` (sync)
 *
 * Both always return a non-empty user-friendly string so a caller can
 * write `toast.error(await extractApiErrorMessage(res, 'Erro ao salvar'))`
 * without checking for null.
 */

const NETWORK_FALLBACK = 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';

/**
 * Reads a fetch Response body and returns the best human-readable message.
 * Handles FastAPI shapes:
 *   - { detail: "string" }              (HTTPException with string detail)
 *   - { detail: [{ msg, loc, ... }] }   (422 ValidationError)
 *   - { message: "string" }             (some external proxies)
 * Falls back to status-code-specific messages, then to the provided fallback.
 *
 * @param {Response} res     - The fetch Response (already received, !res.ok)
 * @param {string}   fallback - Message to use when nothing better can be derived
 * @returns {Promise<string>}
 */
export async function extractApiErrorMessage(res, fallback) {
  // Try to parse JSON body
  try {
    const data = await res.clone().json();
    if (data) {
      if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
      if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
      if (typeof data.message === 'string' && data.message.trim()) return data.message;
      if (typeof data.error === 'string' && data.error.trim()) return data.error;
    }
  } catch (_) {
    /* body not JSON, fall through */
  }

  // Status-specific friendly defaults (only used when body has no detail)
  switch (res.status) {
    case 401: return 'Sessão expirada. Faça login novamente.';
    case 403: return 'Você não tem permissão para esta ação.';
    case 404: return 'Recurso não encontrado.';
    case 408: return 'O servidor demorou a responder. Tente novamente.';
    case 413: return 'Arquivo muito grande.';
    case 429: return 'Muitas solicitações. Aguarde alguns instantes e tente novamente.';
    case 502:
    case 503:
    case 504: return 'Servidor temporariamente indisponível. Tente novamente em alguns segundos.';
    default:  return fallback;
  }
}

/**
 * Returns a friendly message for a thrown fetch/axios error (no response).
 * Detects abort, network failure, and CORS.
 *
 * @param {unknown} error  - The error caught in the catch block (optional)
 * @param {string}  [fallback]
 * @returns {string}
 */
export function networkErrorMessage(error, fallback = NETWORK_FALLBACK) {
  if (!error) return fallback;
  const name = error?.name || '';
  const msg = (error?.message || '').toLowerCase();

  if (name === 'AbortError' || msg.includes('aborted')) {
    return 'A requisição foi cancelada. Tente novamente.';
  }
  if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network error')) {
    return NETWORK_FALLBACK;
  }
  return fallback;
}
